import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Skeleton,
    TextField,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { serverTimestamp } from 'firebase/firestore';
import { motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ApplicationButton from '../../../../Components/ApplicationButton';
import { useMicrospecDynamicUIContext } from '../../../../GlobalProvider';
import { getOrDefault } from '../../../../Helpers/Utils';
import { saveDocument } from '../../../../Helpers/dataUtils';
import { ContactFormProps, FieldOption, FormData, FormErrors, FormField } from './types';
import { useMicrospecThemeLanguageContext } from '../../../../Themes';
import { TextDescriptor } from '../../../../Models/Common';

const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: 16,
    boxShadow: theme.palette.mode === 'dark'
        ? '0 8px 32px rgba(0,0,0,0.5)'
        : '0 8px 32px rgba(0,0,0,0.12)',
    border: theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.1)'
        : 'none',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    '&:hover': {
        boxShadow: theme.palette.mode === 'dark'
            ? '0 16px 48px rgba(0,0,0,0.7)'
            : '0 16px 48px rgba(0,0,0,0.18)',
    }
}));

// Helper function to get display name from TextDescriptor
const getDisplayName = (descriptor: TextDescriptor | undefined, localizationProvider: any): string => {
    if (!descriptor) return '';
    return localizationProvider.translateByType(descriptor.localizationObject);
};

// Helper function to determine if a color is dark
const isDarkColor = (color: string): boolean => {
    if (!color || color === 'transparent') return false;

    // Convert color to RGB values
    let r: number | undefined, g: number | undefined, b: number | undefined;

    // Handle hex colors
    if (color.startsWith('#')) {
        const hex = color.replace('#', '');
        if (hex.length === 3) {
            r = parseInt(hex[0] + hex[0], 16);
            g = parseInt(hex[1] + hex[1], 16);
            b = parseInt(hex[2] + hex[2], 16);
        } else if (hex.length === 6) {
            r = parseInt(hex.substring(0, 2), 16);
            g = parseInt(hex.substring(2, 4), 16);
            b = parseInt(hex.substring(4, 6), 16);
        }
    }
    // Handle rgb/rgba colors
    else if (color.startsWith('rgb')) {
        const matches = color.match(/\d+/g);
        if (matches && matches.length >= 3) {
            r = parseInt(matches[0]);
            g = parseInt(matches[1]);
            b = parseInt(matches[2]);
        }
    }
    // Handle named colors (basic ones)
    else {
        const namedColors: Record<string, [number, number, number]> = {
            black: [0, 0, 0],
            white: [255, 255, 255],
            red: [255, 0, 0],
            green: [0, 128, 0],
            blue: [0, 0, 255],
            navy: [0, 0, 128],
            darkblue: [0, 0, 139],
            darkgreen: [0, 100, 0],
            darkred: [139, 0, 0],
            purple: [128, 0, 128],
            maroon: [128, 0, 0],
            gray: [128, 128, 128],
            grey: [128, 128, 128],
            darkgray: [169, 169, 169],
            darkgrey: [169, 169, 169],
            silver: [192, 192, 192]
        };

        const colorValues = namedColors[color.toLowerCase()];
        if (colorValues) {
            [r, g, b] = colorValues;
        }
    }

    // If we couldn't parse the color, assume it's light
    if (r === undefined || g === undefined || b === undefined) return false;

    // Calculate luminance using the standard formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return true if luminance is less than 0.5 (dark color)
    return luminance < 0.5;
};

// Helper function to get skeleton color based on background
const getSkeletonColor = (backgroundColor: string): string => {
    return isDarkColor(backgroundColor)
        ? 'rgba(255, 255, 255, 0.3)'
        : 'rgba(0, 0, 0, 0.11)';
};

// Validation mapper
type ValidationFunction = (field: FormField, value: string | number) => string | null;

const validationMappers: Record<string, ValidationFunction> = {
    text: (field, value) => {
        const strValue = String(value);
        if (field.required && (!strValue || strValue.trim() === '')) {
            return 'This field is required';
        }
        if (field.regex && strValue && !new RegExp(field.regex).test(strValue)) {
            return 'Invalid format';
        }
        return null;
    },
    numeric: (field, value) => {
        if (field.required && (value === '' || value === null || value === undefined)) {
            return 'This field is required';
        }
        if (value !== '' && value !== null && value !== undefined) {
            const numValue = Number(value);
            if (isNaN(numValue)) {
                return 'Must be a valid number';
            }
            if (field.minValue !== undefined && numValue < field.minValue) {
                return `Must be at least ${field.minValue}`;
            }
            if (field.maxValue !== undefined && numValue > field.maxValue) {
                return `Must be at most ${field.maxValue}`;
            }
        }
        return null;
    },
    dropdown: (field, value) => {
        if (field.required && (!value || value === '')) {
            return 'This field is required';
        }
        return null;
    }
};

// Field type mappers
interface FieldMapper {
    component: React.ComponentType<any>;
    getProps: (field: FormField, value: string | number, onChange: (key: string, value: string | number) => void, error?: string | null, localizationProvider?: any) => any;
}

const fieldTypeMappers: Record<string, FieldMapper> = {
    text: {
        component: TextField,
        getProps: (field, value, onChange, error, localizationProvider) => ({
            type: 'text',
            fullWidth: true,
            variant: 'outlined' as const,
            value: value || '',
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange(field.key, e.target.value),
            error: !!error,
            helperText: error,
            required: field.required || false,
            multiline: field.multiline || false,
            rows: field.multiline ? 4 : 1,
            disabled: field.disabled || false,
            inputProps: field.regex ? { pattern: field.regex } : {},
        })
    },
    numeric: {
        component: TextField,
        getProps: (field, value, onChange, error, localizationProvider) => ({
            type: 'number',
            fullWidth: true,
            variant: 'outlined' as const,
            value: value || '',
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                const numValue = parseFloat(e.target.value);
                onChange(field.key, isNaN(numValue) ? '' : numValue);
            },
            error: !!error,
            helperText: error,
            required: field.required || false,
            disabled: field.disabled || false,
            inputProps: {
                min: field.minValue !== undefined ? field.minValue : undefined,
                max: field.maxValue !== undefined ? field.maxValue : undefined,
                step: 'any'
            },
        })
    },
    dropdown: {
        component: FormControl,
        getProps: (field, value, onChange, error, localizationProvider) => ({
            fullWidth: true,
            variant: 'outlined' as const,
            error: !!error,
            required: field.required || false,
            disabled: field.disabled || false,
            children: [
                <InputLabel key="label" required={field.required || false}>
                    {getDisplayName(field.label, localizationProvider)}
                </InputLabel>,
                <Select
                    key="select"
                    value={String(value || '')}
                    onChange={(e: SelectChangeEvent) => onChange(field.key, e.target.value)}
                    label={getDisplayName(field.label, localizationProvider)}
                >
                    {field.setOfValues?.map((option: FieldOption, index: number) => (
                        <MenuItem key={index} value={option.value}>
                            {getDisplayName(option.label, localizationProvider)}
                        </MenuItem>
                    ))}
                </Select>,
                error && <FormHelperText key="helper">{error}</FormHelperText>
            ]
        })
    }
};

const ContactForm: React.FC<ContactFormProps> = ({ spec, type, index }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { cloudProvider } = useMicrospecDynamicUIContext();
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const { firestore } = cloudProvider;

    // Component configuration
    const componentSpec = getOrDefault(spec, {});
    const sectionBackgroundColor = getOrDefault(componentSpec.sectionBackgroundColor, theme.palette.background.default);
    const sectionTitleObject = getOrDefault(componentSpec.sectionTitle?.localizationObject, { type: "Internal", text: 'contactUs' });
    const sectionTitle = localizationProvider.translateByType(sectionTitleObject);
    const titleColor = getOrDefault(componentSpec.titleColor, theme.palette.text.primary);

    const formCardBackgroundColor = getOrDefault(componentSpec.formCardBackgroundColor, theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff');

    const submitButtonTextObject = getOrDefault(componentSpec.submitButtonText?.localizationObject, { type: "Internal", text: 'submit' });
    const submitButtonText = localizationProvider.translateByType(submitButtonTextObject);
    const submitButtonTextColor = getOrDefault(componentSpec.submitButtonTextColor, "primary.contrastText");
    const submitButtonBackground = getOrDefault(componentSpec.submitButtonBackground, "primary.main");
    const submitButtonVariant = getOrDefault(componentSpec.submitButtonVariant, "contained");

    const submittingText = localizationProvider.translateByType({ type: "Internal", text: 'submitting' });

    const clearButtonTextObject = getOrDefault(componentSpec.clearButtonText?.localizationObject, { type: "Internal", text: 'clear' });
    const clearButtonText = localizationProvider.translateByType(clearButtonTextObject);
    const clearButtonTextColor = getOrDefault(componentSpec.clearButtonTextColor, theme.palette.primary.main);
    const clearButtonBackground = getOrDefault(componentSpec.clearButtonBackground, "transparent");
    const clearButtonVariant = getOrDefault(componentSpec.clearButtonVariant, "outlined");

    const successMessageObject = getOrDefault(componentSpec.successMessage?.localizationObject, { type: "Internal", text: 'formSuccessMessage' });
    const successMessage = localizationProvider.translateByType(successMessageObject);
    const collectionToStoreTo = getOrDefault(componentSpec.collectionToStoreTo, 'contactFormSubmissions');
    const fields = getOrDefault(componentSpec.fields, []) as FormField[];

    const rootSx = getOrDefault(componentSpec.rootSx, {});

    useMemo(() => {
        fields.forEach((field, idx) => {
            if (!field.key || field.key.trim() === '') {
                throw new Error(`Form field at index ${idx} is missing a 'key' property.`);
            }
        });
    }, [fields]);
    const loading = getOrDefault(componentSpec.loading, false);

    // State
    const [formData, setFormData] = useState<FormData>(() => {
        const initialData: FormData = {};
        fields.forEach((field: FormField) => {
            if (field.defaultValue !== undefined) {
                initialData[field.key] = field.defaultValue;
            } else if (field.type === 'dropdown' && field.defaultValueIndex !== undefined) {
                const defaultOption = field.setOfValues?.[field.defaultValueIndex];
                initialData[field.key] = defaultOption?.value || '';
            } else {
                initialData[field.key] = field.type === 'numeric' ? 0 : '';
            }
        });
        return initialData;
    });

    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
            if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
        };
    }, []);

    // Handle field value change
    const handleFieldChange = useCallback((key: string, value: string | number) => {
        setFormData(prev => ({
            ...prev,
            [key]: value
        }));

        // Clear error for this field when user starts typing
        setErrors(prev => {
            if (prev[key]) {
                const newErrors = { ...prev };
                delete newErrors[key];
                return newErrors;
            }
            return prev;
        });
    }, []); // Remove errors from dependency array

    // Validate form
    const validateForm = useCallback((): boolean => {
        const newErrors: FormErrors = {};
        let isValid = true;

        fields.forEach((field: FormField) => {
            const validator = validationMappers[field.type];
            if (validator) {
                const error = validator(field, formData[field.key]);
                if (error) {
                    newErrors[field.key] = error;
                    isValid = false;
                }
            }
        });

        setErrors(newErrors);
        return isValid;
    }, [fields, formData]);

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }
        setIsSubmitting(true);

        try {
            const submissionData = {
                ...formData,
                submittedAt: serverTimestamp(),
                status: 'toProcess'
            };

            await saveDocument(firestore, collectionToStoreTo, submissionData);
            setShowSuccess(true);
            setShowError(false);
            handleClear();

            // Hide success message after 5 seconds
            if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
            successTimeoutRef.current = setTimeout(() => {
                setShowSuccess(false);
            }, 5000);
        } catch (error) {
            console.error('Error submitting form:', error);
            setShowError(true);
            if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
            errorTimeoutRef.current = setTimeout(() => {
                setShowError(false);
            }, 6000);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle form clear
    const handleClear = useCallback(() => {
        const clearedData: FormData = {};
        fields.forEach((field: FormField) => {
            if (field.defaultValue !== undefined) {
                clearedData[field.key] = field.defaultValue;
            } else if (field.type === 'dropdown' && field.defaultValueIndex !== undefined) {
                const defaultOption = field.setOfValues?.[field.defaultValueIndex];
                clearedData[field.key] = defaultOption?.value || '';
            } else {
                clearedData[field.key] = field.type === 'numeric' ? 0 : '';
            }
        });
        setFormData(clearedData);
        setErrors({});
    }, [fields]);

    // Render field component
    const renderField = (field: FormField) => {
        if (field.hidden) return null;

        const mapper = fieldTypeMappers[field.type];
        if (!mapper) {
            console.warn(`Unknown field type: ${field.type}`);
            return null;
        }

        const Component = mapper.component;
        const props = mapper.getProps(
            field,
            formData[field.key],
            handleFieldChange,
            errors[field.key],
            localizationProvider
        );

        return (
            <Box key={field.key} sx={{ mb: 3 }}>
                {field.type !== 'dropdown' && (
                    <Component
                        label={getDisplayName(field.label, localizationProvider)}
                        {...props}
                    />
                )}
                {field.type === 'dropdown' && (
                    <Component {...props} />
                )}
            </Box>
        );
    };

    // Render skeleton form
    const renderSkeletonForm = () => {
        const skeletonColor = getSkeletonColor(formCardBackgroundColor);

        return (
            <Box sx={{ mb: 3 }}>
                {/* Skeleton fields */}
                {Array.from({ length: 5 }).map((_, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                        <Skeleton
                            variant="text"
                            width="30%"
                            height={20}
                            sx={{
                                mb: 1,
                                backgroundColor: skeletonColor
                            }}
                        />
                        <Skeleton
                            variant="rectangular"
                            width="100%"
                            height={56}
                            sx={{
                                borderRadius: 1,
                                backgroundColor: skeletonColor
                            }}
                        />
                    </Box>
                ))}

                {/* Skeleton buttons */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        justifyContent: 'center',
                        flexDirection: isMobile ? 'column' : 'row',
                        mt: 4
                    }}
                >
                    <Skeleton
                        variant="rectangular"
                        width={isMobile ? '100%' : 140}
                        height={48}
                        sx={{
                            borderRadius: 2,
                            backgroundColor: skeletonColor
                        }}
                    />
                    <Skeleton
                        variant="rectangular"
                        width={isMobile ? '100%' : 140}
                        height={48}
                        sx={{
                            borderRadius: 2,
                            backgroundColor: skeletonColor
                        }}
                    />
                </Box>
            </Box>
        );
    };

    return (
        <Box
            id={spec.id}
            sx={{
                background: sectionBackgroundColor,
                minHeight: '50vh',
                py: 6,
                px: { xs: 2, md: 4 },
                ...rootSx
            }}
        >
            {/* Section Title */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, ease: "easeOut" as const }}
            >
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    {loading ? (
                        <Skeleton
                            variant="text"
                            width="30%"
                            height={60}
                            sx={{
                                mx: 'auto',
                                backgroundColor: getSkeletonColor(sectionBackgroundColor)
                            }}
                        />
                    ) : (
                        sectionTitle && (
                            <Typography
                                variant="h3"
                                sx={{
                                    color: titleColor,
                                    fontWeight: 800,
                                    fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
                                    lineHeight: 1.2,
                                    mb: 2
                                }}
                            >
                                {sectionTitle}
                            </Typography>
                        )
                    )}
                </Box>
            </motion.div>

            {/* Contact Form */}
            <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: "easeOut" as const, delay: 0.2 }}
            >
                <Box
                    sx={{
                        maxWidth: 600,
                        mx: 'auto',
                    }}
                >
                    <StyledCard sx={{ backgroundColor: formCardBackgroundColor }}>
                        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                            {loading ? (
                                renderSkeletonForm()
                            ) : (
                                <form onSubmit={handleSubmit}>
                                    {/* Dynamic Fields */}
                                    {fields.map(renderField)}

                                    {/* Action Buttons */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 2,
                                            justifyContent: 'center',
                                            flexDirection: isMobile ? 'column' : 'row',
                                            mt: 4
                                        }}
                                    >
                                        <ApplicationButton
                                            type="submit"
                                            disabled={isSubmitting}
                                            variant={submitButtonVariant}
                                            sx={{
                                                color: submitButtonTextColor,
                                                backgroundColor: submitButtonBackground
                                            }}
                                            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                                        >
                                            {isSubmitting ? submittingText : submitButtonText}
                                        </ApplicationButton>

                                        <ApplicationButton
                                            onClick={handleClear}
                                            disabled={isSubmitting}
                                            variant={clearButtonVariant}
                                            sx={{
                                                color: clearButtonTextColor,
                                                backgroundColor: clearButtonBackground,
                                                borderColor: clearButtonTextColor,
                                                '&:hover': {
                                                    color: theme.palette.mode === 'dark'
                                                        ? theme.palette.common.white
                                                        : theme.palette.common.white,
                                                    backgroundColor: clearButtonTextColor,
                                                    borderColor: clearButtonTextColor
                                                }
                                            }}
                                        >
                                            {clearButtonText}
                                        </ApplicationButton>
                                    </Box>
                                </form>
                            )}
                        </CardContent>
                    </StyledCard>

                    {/* Success Message */}
                    {showSuccess && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3, ease: "easeOut" as const }}
                        >
                            <Box sx={{ mt: 3 }}>
                                <Alert
                                    severity="success"
                                    variant="filled"
                                    sx={{
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                                    }}
                                >
                                    {successMessage}
                                </Alert>
                            </Box>
                        </motion.div>
                    )}

                    {/* Error Message */}
                    {showError && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3, ease: "easeOut" as const }}
                        >
                            <Box sx={{ mt: 3 }}>
                                <Alert
                                    severity="error"
                                    variant="filled"
                                    sx={{
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)'
                                    }}
                                >
                                    {localizationProvider.translateByType({ type: "Internal", text: 'formErrorMessage' }) || 'Something went wrong. Please try again.'}
                                </Alert>
                            </Box>
                        </motion.div>
                    )}
                </Box>
            </motion.div>
        </Box>
    );
};

export default ContactForm;
